import { Box, Chip, type SxProps, type Theme } from '@mui/material';
import { type ReactNode, startTransition, useCallback, useMemo } from 'react';
import { useShallow } from 'zustand/shallow';
import { ClearAllButton } from '../ClearAllButton';

type StickyBarStoreProps<TFilterState extends Record<string, string | string[]>, TSortValue> = <T>(
  selector: (state: {
    handleClearFilters: () => void;
    appliedFilters: TFilterState | null;
    sortBy: TSortValue;
  }) => T,
  equalityFn?: (a: T, b: T) => boolean,
) => T;

type Props<TFilterState extends Record<string, string | string[]>, TSortValue> = {
  showStickyBar: boolean;
  useStore: StickyBarStoreProps<TFilterState, TSortValue>;
  children: ReactNode;
  clearButtonText?: string;
  scrollBackToMainBar: () => void;
  onClearFilters?: () => void;
  clearAllFiltersEvent: () => void;
  initialClientRegion?: string;
  isFiltersInHeader?: boolean;
};

const FILTERS_IN_HEADER_CHILDREN_SX: SxProps<Theme> = {
  flex: 1,
  gap: '16px',
  '& > *': { flex: 1 },
};

export const StickyBar = <TFilterState extends Record<string, string | string[]>, TSortValue>({
  showStickyBar,
  useStore,
  children,
  clearButtonText = 'Clear All',
  scrollBackToMainBar,
  onClearFilters,
  clearAllFiltersEvent,
  initialClientRegion,
  isFiltersInHeader,
}: Props<TFilterState, TSortValue>) => {
  const { handleClearFilters, appliedFilters, sortBy } = useStore(
    useShallow(state => ({
      handleClearFilters: state.handleClearFilters,
      appliedFilters: state.appliedFilters,
      sortBy: state.sortBy,
    })),
  );

  const effectiveOnClearFilters = onClearFilters || handleClearFilters;
  const onClearFiltersHandler = useCallback(() => {
    scrollBackToMainBar();
    setTimeout(() => {
      startTransition(() => {
        effectiveOnClearFilters();
        clearAllFiltersEvent();
      });
    }, 100);
  }, [effectiveOnClearFilters, clearAllFiltersEvent, scrollBackToMainBar]);

  const numberOfAppliedFilters = useMemo(
    () =>
      appliedFilters
        ? Object.values(appliedFilters).reduce((acc: number, currentValue: string | string[]) => {
            if (Array.isArray(currentValue)) {
              return acc + currentValue.length;
            }
            if (currentValue === initialClientRegion) {
              return acc;
            }
            return acc + (currentValue ? 1 : 0);
          }, 0)
        : 0,
    [appliedFilters, initialClientRegion],
  );

  const hasActiveSelections = numberOfAppliedFilters > 0 || !!sortBy;

  return (
    <Box
      sx={{
        backgroundColor: 'white',
        boxShadow: '0px 4px 4px 0px rgba(0, 0, 0, 0.15)',
        width: '100%',
        py: '24px',
        display: { xs: 'none', sm: showStickyBar ? 'flex' : 'none' },
        position: 'fixed',
        top: 0,
        left: 0,
        zIndex: 200,
      }}
    >
      <Box
        sx={{
          pl: '14px',
          maxWidth: '1440px',
          width: '100%',
          ml: 'auto',
          mr: 'auto',
          pr: { sm: '14px' },
        }}
      >
        <Box
          sx={{
            maxWidth: { md: '82%' },
            ml: { md: 'auto' },
            mr: { md: 'auto' },
            display: 'flex',
            flexDirection: 'column',
            gap: '16px',
          }}
        >
          <Box
            sx={{
              display: 'flex',
              justifyContent: 'space-between',
              flexDirection: 'row',
              marginLeft: 'auto',
              marginRight: 'auto',
              width: '100%',
              gap: isFiltersInHeader ? 3 : 0,
            }}
          >
            <Box
              sx={
                [
                  { display: 'flex', color: 'secondary.main', gap: '8px' },
                  ...(isFiltersInHeader ? [FILTERS_IN_HEADER_CHILDREN_SX] : []),
                ] as SxProps<Theme>
              }
            >
              {children}
            </Box>
            {isFiltersInHeader ? (
              <ClearAllButton
                onClick={onClearFiltersHandler}
                disabled={!hasActiveSelections}
                sx={{ ml: 2 }}
              />
            ) : (
              <Chip
                label={clearButtonText}
                variant="filterPills"
                onClick={onClearFiltersHandler}
                disabled={!numberOfAppliedFilters && !sortBy}
                sx={{
                  fontSize: { xs: 12, sm: 14 },
                }}
              />
            )}
          </Box>
        </Box>
      </Box>
    </Box>
  );
};
